<template>
  <section class="container" v-if="match" style="width: 80%; margin: 0 auto">
    <p v-show="flags.isProcessing" class="has-text-weight-bold is-size-7">
      Processing game with Stockfish engine...
    </p>
    <b-progress
      v-show="flags.isProcessing"
      type="is-warning"
      :value="progress"
      show-value
      format="percent"
    ></b-progress>

    <!-- Accuracy Scores Display -->
    <div
      v-if="accuracyScores"
      class="accuracy-scores-section has-background-light p-4 my-5"
    >
      <h3 class="title is-4 mb-4 has-text-dark">🎯 Game Accuracy Analysis</h3>
      <div class="columns is-multiline">
        <div class="column is-6">
          <div
            class="accuracy-card has-background-white p-4 has-text-centered has-shadow"
          >
            <div class="has-text-grey-light is-size-7 mb-2">White Player</div>
            <div class="title is-2 has-text-dark">
              {{ Math.round(accuracyScores.player1Accuracy) }}%
            </div>
            <div class="has-text-grey is-size-7">
              {{
                match.player1
                  ? `${match.player1.first_name} ${match.player1.last_name}`
                  : "Player 1"
              }}
            </div>
          </div>
        </div>
        <div class="column is-6">
          <div
            class="accuracy-card has-background-white p-4 has-text-centered has-shadow"
          >
            <div class="has-text-grey-light is-size-7 mb-2">Black Player</div>
            <div class="title is-2 has-text-dark">
              {{ Math.round(accuracyScores.player2Accuracy) }}%
            </div>
            <div class="has-text-grey is-size-7">
              {{
                match.player2
                  ? `${match.player2.first_name} ${match.player2.last_name}`
                  : "Player 2"
              }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Move Evaluations Table -->
    <div class="columns is-multiline" v-show="moveEvals.length > 0">
      <div class="column is-6" v-if="match.player1">
        {{ match.player1.first_name }} {{ match.player1.last_name }}
      </div>
      <div class="column is-6" v-if="match.player2">
        {{ match.player2.first_name }} {{ match.player2.last_name }}
      </div>

      <div
        class="column is-6 move-eval-item has-border"
        v-for="r in moveEvals"
        :key="'moveEval' + r.number"
      >
        {{ r.move }}
        <strong class="has-text-dark"
          >({{ r.score | asChessEvalScore(r.mateValue) }})</strong
        >
      </div>
    </div>
  </section>
</template>

<script>
import GameEval from "../../libs/ai/GameEval";
import { calculateAccuracyScores } from "vue-chess-shared";

export default {
  name: "GameAnalysis",
  props: ["moves", "match"],

  data() {
    return {
      flags: {
        isProcessing: false,
      },
      movesForEval: [],
      moveEvals: [],
      accuracyScores: null, // Store accuracy scores
    };
  },

  computed: {
    progress() {
      const p = (this.moveEvals.length / this.movesForEval.length) * 100;
      if (p === Infinity) return 0;
      return Math.round(p);
    },
  },

  methods: {
    async loadData() {
      if (!this.moves || !this.moves.length) return;
      if (this.flags.isProcessing) return;

      this.flags.isProcessing = true;
      this.moveEvals = [];

      this.movesForEval = this.moves
        .sort((a, b) => a.move_number - b.move_number)
        .map((m) => `${m.orig}${m.dest}`);

      const gameEval = new GameEval();

      gameEval.onReady = () => {
        console.log("#### First process done! Now ready");

        // Evaluate the first move
        gameEval.sendMove(this.movesForEval[0]);

        gameEval.onMoveComplete = (moveEval) => {
          // Add to UI (keep original evaluation data for accuracy calculation)
          this.moveEvals.push(moveEval);

          // Evaluate the next move
          if (moveEval.number < this.movesForEval.length) {
            gameEval.sendMove(this.movesForEval[moveEval.number]);
            return;
          }

          // all moves have already been evaluated
          this.flags.isProcessing = false;

          this.processGameAccuracy();
        };
      };
    },

    processGameAccuracy() {
      // Pass raw evaluations to accuracy calculation as a comma-separated string
      const allEvaluations = this.moveEvals
        .map((m) => m.rawEvaluation)
        .join(",");

      // Calculate accuracy scores for both players
      const accuracyScores = calculateAccuracyScores(allEvaluations);

      // Store accuracy scores for UI display
      this.accuracyScores = accuracyScores;
    },
  },
};
</script>

<style scoped>
.accuracy-scores-section {
  border-radius: 8px;
}

.accuracy-card {
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.move-eval-item {
  border: 1px solid #cccccc;
  padding: 8px;
  margin: 2px 0;
  background-color: #fafafa;
}
</style>
